import { MutableRefObject, useEffect, useRef } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useTabIsActive from 'src/hooks/utils/useTabIsActive';

export interface SubscriptionTabRecoveryTiming {
	/**
	 * Cooldown after the last fallback (per `lastFallbackTimestamp`) before
	 * another recovery attempt is allowed.
	 */
	fallbackCooldownMs: number;
	/** Cadence of the periodic recovery check while the tab is visible. */
	recoveryCheckIntervalMs: number;
	/**
	 * How long the tab must stay inactive before `onTabInactiveCleanup` fires
	 * and the transport is torn down. Acts as a debounce: quick tab switches
	 * never disconnect.
	 */
	tabInactiveDisconnectDelayMs: number;
	/**
	 * Max number of consecutive recovery attempts before we give up and stay on
	 * the fallback source. Without this ceiling the recovery loop retries
	 * forever, spamming the console when the websocket server is unreachable.
	 * The counter is reset on a healthy transport or when the tab is refocused.
	 */
	maxRecoveryAttempts: number;
}

export interface UseSubscriptionTabRecoveryParams {
	/**
	 * Whether the transport is currently on its primary (healthy) source, e.g.
	 * websocket rather than polling. A healthy transport resets the recovery
	 * attempt counter; an unhealthy one is eligible for recovery attempts.
	 */
	isHealthy: boolean;
	/**
	 * Whether automatic recovery is allowed. Pass false when the user has
	 * manually pinned a source (e.g. auto source switching disabled). Defaults
	 * to true. When false, recovery attempts are suppressed entirely.
	 */
	recoveryEnabled?: boolean;
	/**
	 * Timestamp (ms) of the transport's last fallback, owned by the transport
	 * (its pipeline sets it on timeout/error). Zeroed by this hook when a
	 * periodic recovery attempt fires, so a failed attempt re-arms the cooldown
	 * only when the transport falls back again.
	 */
	lastFallbackTimestamp: MutableRefObject<number>;
	/**
	 * Tear down and rebuild the transport pipeline. Called by the periodic and
	 * eager recovery checks, and — unless `onTabActiveReconnect` is provided —
	 * on tab refocus after a disconnect.
	 */
	performRecovery: () => void;
	/**
	 * Tear down the transport after the tab has been inactive for
	 * `tabInactiveDisconnectDelayMs`. Must NOT trigger a reconnect; the
	 * reconnect happens when the tab becomes active again.
	 */
	onTabInactiveCleanup: () => void;
	/**
	 * Optional override for the full reconnect performed when the tab becomes
	 * active after the inactive disconnect fired. Defaults to `performRecovery`.
	 */
	onTabActiveReconnect?: () => void;
	/**
	 * Opt-in gate for "eager" recovery attempts, which fire when transport
	 * health/enablement changes while the tab stays active (rather than waiting
	 * for the next periodic check). Eager attempts do NOT zero
	 * `lastFallbackTimestamp`, so the gate must return false while a recovery
	 * is already in flight (e.g. no active subscription yet) — otherwise a
	 * zeroed timestamp from a prior periodic attempt would let attempts fire
	 * back-to-back before the transport's timeout verdict. When omitted, no
	 * eager attempts are made and recovery is purely interval-driven.
	 */
	canAttemptEagerRecovery?: () => boolean;
	timing: SubscriptionTabRecoveryTiming;
}

/**
 * Shared tab-visibility + recovery module for websocket-style subscriptions.
 *
 * One algorithm, parameterized by transport:
 * - Debounced tab-inactive disconnect: after the tab has been hidden for
 *   `tabInactiveDisconnectDelayMs`, tear the transport down (memory/socket
 *   hygiene). Quick tab switches cancel the pending teardown.
 * - Tab refocus: cancel a pending teardown if it hasn't fired, otherwise do a
 *   full reconnect. Refocusing always re-arms the recovery attempt budget.
 * - Periodic recovery: while the tab is visible and the transport is unhealthy
 *   (and recovery is enabled), attempt recovery every
 *   `recoveryCheckIntervalMs`, gated by `fallbackCooldownMs` since the last
 *   fallback and capped at `maxRecoveryAttempts` consecutive attempts.
 *
 * FE-4066 (recovery race): the original orderbook implementation's visibility
 * effect depended on the pipeline's `destroyCurrentPipeline$` Subject, which is
 * recreated on every recovery. The effect re-run mid-recovery interfered with
 * the ongoing recovery and left the transport stuck in polling mode. The fix
 * was to depend on stable callbacks instead of the Subject. This module
 * enforces that structurally: it never sees the Subject, and every callback is
 * held behind a synced ref, so NO effect here re-fires from callback identity
 * churn (e.g. a provider recreating `performRecovery` when its pipeline
 * Subject is recreated). Effects re-run only on genuine state changes
 * (visibility, health, enablement, timing).
 */
export function useSubscriptionTabRecovery({
	isHealthy,
	recoveryEnabled = true,
	lastFallbackTimestamp,
	performRecovery,
	onTabInactiveCleanup,
	onTabActiveReconnect,
	canAttemptEagerRecovery,
	timing,
}: UseSubscriptionTabRecoveryParams): void {
	const isTabActive = useTabIsActive();
	const wasTabActiveRef = useRef(isTabActive);
	const inactiveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Number of consecutive failed recovery cycles. Reset on a healthy
	// transport or on tab refocus.
	const recoveryAttemptsRef = useRef(0);

	// FE-4066: callbacks live behind refs so effect dependency arrays below
	// contain no callback identities. See hook JSDoc.
	const performRecoveryRef = useSyncedRef(performRecovery);
	const onTabInactiveCleanupRef = useSyncedRef(onTabInactiveCleanup);
	const onTabActiveReconnectRef = useSyncedRef(onTabActiveReconnect);
	const canAttemptEagerRecoveryRef = useSyncedRef(canAttemptEagerRecovery);

	const {
		fallbackCooldownMs,
		recoveryCheckIntervalMs,
		tabInactiveDisconnectDelayMs,
		maxRecoveryAttempts,
	} = timing;

	// Reset the recovery counter whenever the transport reports healthy, so a
	// future disconnect gets a fresh set of recovery attempts.
	useEffect(() => {
		if (isHealthy) {
			recoveryAttemptsRef.current = 0;
		}
	}, [isHealthy]);

	// Tab visibility handling
	useEffect(() => {
		const tabJustBecameActive = isTabActive && !wasTabActiveRef.current;
		const tabJustBecameInactive = !isTabActive && wasTabActiveRef.current;
		wasTabActiveRef.current = isTabActive;

		// Tab became inactive: schedule the debounced disconnect
		if (tabJustBecameInactive) {
			if (inactiveTimeoutRef.current) clearTimeout(inactiveTimeoutRef.current);

			inactiveTimeoutRef.current = setTimeout(() => {
				onTabInactiveCleanupRef.current();
				inactiveTimeoutRef.current = null;
				// No reconnect here — it happens when the tab becomes active again.
			}, tabInactiveDisconnectDelayMs);
			return;
		}

		// Tab became active: cancel the pending disconnect, or fully reconnect
		if (tabJustBecameActive) {
			// Refocusing re-arms recovery even if we'd previously given up.
			recoveryAttemptsRef.current = 0;

			if (inactiveTimeoutRef.current) {
				// Disconnect never fired — transport is still alive, nothing to do.
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
				return;
			}

			// We disconnected while inactive — full reconnect.
			(onTabActiveReconnectRef.current ?? performRecoveryRef.current)();
			return;
		}

		// Eager recovery check: the effect re-ran while the tab stayed active
		// (health or enablement changed). Opt-in via canAttemptEagerRecovery —
		// see its JSDoc for why this is not safe by default.
		if (
			isTabActive &&
			canAttemptEagerRecoveryRef.current &&
			canAttemptEagerRecoveryRef.current() &&
			!isHealthy &&
			recoveryEnabled &&
			recoveryAttemptsRef.current < maxRecoveryAttempts
		) {
			const timeSinceLastFallback = Date.now() - lastFallbackTimestamp.current;
			if (timeSinceLastFallback > fallbackCooldownMs) {
				recoveryAttemptsRef.current += 1;
				performRecoveryRef.current();
			}
		}
	}, [
		isTabActive,
		isHealthy,
		recoveryEnabled,
		tabInactiveDisconnectDelayMs,
		fallbackCooldownMs,
		maxRecoveryAttempts,
		lastFallbackTimestamp,
		// Synced refs are stable; listed to satisfy exhaustive-deps.
		performRecoveryRef,
		onTabActiveReconnectRef,
		onTabInactiveCleanupRef,
		canAttemptEagerRecoveryRef,
	]);

	// Periodic recovery check
	useEffect(() => {
		const checkRecoveryInterval = setInterval(() => {
			if (document.hidden) return; // Skip recovery attempts when tab is inactive

			const isInFallback = !isHealthy && recoveryEnabled;
			if (!isInFallback) return;

			// Stop retrying once we've exhausted our recovery budget — stay on
			// the fallback source until a healthy connection or tab refocus
			// re-arms us.
			if (recoveryAttemptsRef.current >= maxRecoveryAttempts) return;

			const timeSinceLastFallback = Date.now() - lastFallbackTimestamp.current;
			if (timeSinceLastFallback > fallbackCooldownMs) {
				recoveryAttemptsRef.current += 1;
				lastFallbackTimestamp.current = 0;
				performRecoveryRef.current();
			}
		}, recoveryCheckIntervalMs);

		return () => clearInterval(checkRecoveryInterval);
	}, [
		isHealthy,
		recoveryEnabled,
		recoveryCheckIntervalMs,
		fallbackCooldownMs,
		maxRecoveryAttempts,
		lastFallbackTimestamp,
		performRecoveryRef,
	]);

	// Cleanup inactive timeout on unmount
	useEffect(() => {
		return () => {
			if (inactiveTimeoutRef.current) {
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
			}
		};
	}, []);
}
